import * as Sentry from '@sentry/react';

// ============================================================
// PII scrubbing for breadcrumbs and event request URLs
// ============================================================
// We never want OTP codes, magic-link tokens, or raw emails to leave
// the browser. Sentry's auto-captured fetch/navigation breadcrumbs can
// contain these as query params (e.g. /auth/callback?token=...). This
// scrub runs on every breadcrumb before it ships.
const SENSITIVE_QUERY_PARAMS = [
  'email',
  'otp',
  'code',
  'token',
  'access_token',
  'refresh_token',
  'confirmation_token',
];

function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    let mutated = false;
    SENSITIVE_QUERY_PARAMS.forEach((p) => {
      if (parsed.searchParams.has(p)) {
        parsed.searchParams.set(p, '[redacted]');
        mutated = true;
      }
    });
    return mutated ? parsed.toString() : url;
  } catch {
    return url;
  }
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,

  // Environment tag so you can filter in Sentry dashboard
  environment: process.env.NODE_ENV || 'development',

  // Only send errors in production (set to 1.0 to capture all in staging)
  sampleRate: process.env.NODE_ENV === 'production' ? 1.0 : 1.0,

  // Don't initialize if DSN is not set
  enabled: !!process.env.REACT_APP_SENTRY_DSN,

  // Filter out noisy browser-specific errors.
  // IMPORTANT: Patterns are anchored to filter only bare/unattributed errors.
  // Errors we explicitly capture in the auth-handoff path are wrapped in a
  // distinct message prefix ("[auth-handoff] ..."), so they pass through
  // even when the underlying cause is e.g. "Failed to fetch".
  ignoreErrors: [
    // Safari internal errors
    'EmptyRanges',
    // Common browser extension noise
    /^ResizeObserver loop/,
    // Bare network errors — wrapped errors with auth-handoff prefix still pass
    /^Failed to fetch$/,
    /^NetworkError/,
    /^Load failed$/,
    // React minified errors from ad blockers / browser quirks
    /^Minified React error/,
  ],

  // IMPORTANT: use the callback form to APPEND to default integrations
  // instead of replacing them. Passing a plain array here drops:
  //   * globalHandlersIntegration (window.onerror + unhandledrejection) —
  //     without this, uncaught throws and rejected promises are NEVER
  //     captured (they vanish silently). Only explicit Sentry.captureException
  //     calls and console.error captures still work, which masks real issues.
  //   * breadcrumbsIntegration — fetch/console/navigation/click breadcrumbs
  //   * linkedErrorsIntegration — walks Error.cause chains
  //   * inboundFiltersIntegration — filters known browser noise
  //   * dedupeIntegration — drops duplicate events
  //   * httpContextIntegration — adds request context to events
  //
  // Symptom of the previous (array) form: Sentry shipped pageload transactions
  // and any explicit captureException/console.error calls, but globally
  // thrown exceptions and unhandled promise rejections never appeared.
  // Discovered while diagnosing why post-029 silent-failure users like
  // hectorlugo / robert.peterson830 produced zero Sentry events despite
  // the Task 3 auth-handoff instrumentation being correctly deployed.
  integrations: (defaultIntegrations) => [
    ...defaultIntegrations,
    // Performance monitoring (not in defaults — explicit opt-in required)
    Sentry.browserTracingIntegration(),
    // Mirror console.error calls into Sentry as events
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
  ],

  // Performance monitoring sample rate (0.1 = 10% of transactions)
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

  // Strip sensitive query params from fetch breadcrumb URLs before they're sent.
  // This is the line of defense for OTP codes / magic-link tokens that can
  // appear in URLs (e.g. /auth/callback?token=...).
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.data && typeof breadcrumb.data.url === 'string') {
      breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
    }
    return breadcrumb;
  },

  // Same scrub on the top-level request URL on the error event itself.
  beforeSend(event) {
    if (event.request?.url) {
      event.request.url = scrubUrl(event.request.url);
    }
    return event;
  },
});

export default Sentry;
